import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { brand } from '../theme';
import { gsap, useGsap, QUERIES } from '../animation/gsap';
import { useCompany } from '../content/ContentProvider';

/**
 * The closing call to action.
 *
 * Wording carried over from the PHP theme's template-parts/section-cta.php.
 */
export default function CtaSection() {
	const company = useCompany();

	const root = useGsap(({ mm }) => {
		const q = gsap.utils.selector(root);

		mm.add(QUERIES.motion, () => {
			gsap
				.timeline({ scrollTrigger: { trigger: root.current, start: 'top 80%' } })
				.from(q('[data-cta-title]'), { autoAlpha: 0, y: 30, duration: 0.7, ease: 'power3.out' })
				.from(q('[data-cta-body]'), { autoAlpha: 0, y: 24, duration: 0.6, ease: 'power2.out' }, '-=0.4')
				.from(q('[data-cta-actions] > *'), { autoAlpha: 0, y: 20, duration: 0.5, stagger: 0.1 }, '-=0.35');

			// A slow drift on the gold wash behind, so the block is not completely
			// static while you read it.
			gsap.to(q('[data-cta-glow]'), {
				yPercent: -18,
				ease: 'none',
				scrollTrigger: { trigger: root.current, start: 'top bottom', end: 'bottom top', scrub: 1 },
			});
		});
	}, []);

	return (
		<Box
			ref={root}
			component="section"
			sx={{
				position: 'relative',
				overflow: 'hidden',
				backgroundColor: brand.dark,
				color: brand.textInvert,
				py: { xs: 10, lg: 14 },
				textAlign: 'center',
			}}
		>
			<Box
				data-cta-glow
				aria-hidden="true"
				sx={{
					position: 'absolute',
					bottom: '-40%',
					left: '50%',
					transform: 'translateX(-50%)',
					width: '60rem',
					height: '60rem',
					borderRadius: '50%',
					background: 'radial-gradient(circle, rgb(182 142 75 / 20%) 0%, transparent 62%)',
					pointerEvents: 'none',
				}}
			/>

			<Container sx={{ position: 'relative' }}>
				<Typography
					data-cta-title
					variant="h2"
					component="h2"
					sx={{ color: brand.white, mb: 3, maxWidth: '18ch', mx: 'auto' }}
				>
					Need A Free Estimate?
				</Typography>

				<Typography
					data-cta-body
					sx={{ color: brand.textInvertMuted, fontSize: '1.1rem', maxWidth: '58ch', mx: 'auto', mb: 5 }}
				>
					Tell us what you are planning and we will give you a price with no pressure and no cost.
					We are happy to answer questions even if you are still deciding.
				</Typography>

				<Stack
					data-cta-actions
					direction={{ xs: 'column', sm: 'row' }}
					spacing={2}
					sx={{ justifyContent: 'center' }}
				>
					{/* Sentence case to match every other button. The one action has
					    the one name everywhere: "Get a free estimate" in the header,
					    the hero, the drawer, and here. The heading above keeps the
					    owner's own casing — it is a heading, not a control. */}
					<Button variant="contained" color="primary" size="large" component={RouterLink} to="/contact">
						Get a free estimate
					</Button>
					<Button
						variant="outlined"
						size="large"
						href={company.phone_href}
						sx={{
							color: brand.textInvert,
							borderColor: brand.lineLight,
							'&:hover': { borderColor: brand.accent, color: brand.accent },
						}}
					>
						Call us now
					</Button>
				</Stack>
			</Container>
		</Box>
	);
}
